/**
 * Pronunciation Challenge Exercise (INT-CMP-003)
 * Focused pronunciation exercise on a specific word/sound with
 * common mistake guidance and phonetic hints.
 */

import React, { useState, useCallback } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, Text, VStack, HStack } from '@gluestack-ui/themed';
import { FS_XS, FS_SM, FS_MD } from '../../theme';
import { useExerciseScoring } from '../../hooks/useExerciseScoring';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface PronunciationChallengeExerciseProps {
  prompt: string;
  targetWord: string;
  phoneticGuide?: string;
  commonMistake?: string;
  hint?: string;
  voiceEnabled: boolean;
  minScore?: number;
  onComplete: (correct: boolean, score: number) => void;
}

export const PronunciationChallengeExercise = React.memo(function PronunciationChallengeExercise({
  prompt,
  targetWord,
  phoneticGuide,
  commonMistake,
  hint,
  voiceEnabled,
  minScore = 70,
  onComplete,
}: PronunciationChallengeExerciseProps) {
  const { theme: t, fontScale, ffs } = useDisplayTheme();

  const [showCommonMistake, setShowCommonMistake] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  const handleSelfScore = useCallback((selfScore: number) => {
    setScore(selfScore);
    setCompleted(true);
    onComplete(selfScore >= minScore, selfScore);
  }, [onComplete, minScore]);

  // Keyboard shortcuts: 1/2/3 keys for self-scoring (web only, no-op on native)
  const showScoring = !completed && !voiceEnabled;
  useExerciseScoring([40, 70, 95], handleSelfScore, showScoring);

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: t.card, borderWidth: 1, borderColor: t.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: t.text, ...ffs }}
        >
          {prompt}
        </Text>

        {/* Target word with phonetic */}
        <Box
          p="$4"
          rounded="$xl"
          alignItems="center"
          style={{ backgroundColor: t.primarySoft }}
        >
          <Text
            allowFontScaling={false}
            style={{ fontSize: 28 * fontScale, fontWeight: '800', color: t.primary, ...ffs }}
          >
            {targetWord}
          </Text>
          {phoneticGuide && (
            <Text
              allowFontScaling={false}
              mt="$1"
              style={{ fontSize: FS_MD * fontScale, color: t.textSecondary, fontStyle: 'italic', ...ffs }}
            >
              /{phoneticGuide}/
            </Text>
          )}
        </Box>

        {/* Common mistake */}
        {commonMistake && (
          <TouchableOpacity
            testID="pronunciation-show-mistake"
            accessibilityLabel="Show common mistake"
            accessibilityRole="button"
            onPress={() => setShowCommonMistake(!showCommonMistake)}
            activeOpacity={0.7}
          >
            <Box
              p="$2"
              rounded="$md"
              style={{ backgroundColor: t.isDark ? '#451A03' : '#FEF3C7' }}
            >
              <HStack space="xs" alignItems="center">
                <Text
                  testID="lesson-common-mistake-indicator"
                  accessibilityLabel="Common mistake indicator"
                  accessibilityRole="image"
                  allowFontScaling={false}
                  style={{ fontSize: FS_SM * fontScale }}
                >⚠️</Text>
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: '#92400E', fontWeight: '500', flex: 1, ...ffs }}
                >
                  {showCommonMistake ? commonMistake : 'Tap to see a common mistake'}
                </Text>
              </HStack>
            </Box>
          </TouchableOpacity>
        )}

        {hint && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Self-assessment */}
        {!completed && !voiceEnabled && (
          <VStack space="sm">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, textAlign: 'center', ...ffs }}
            >
              Practice saying the word above, then rate yourself:
            </Text>
            <HStack space="sm" justifyContent="center">
              {[
                { label: 'Needs work', score: 40, color: '#DC2626' },
                { label: 'Getting there', score: 70, color: '#D97706' },
                { label: 'Nailed it!', score: 95, color: '#059669' },
              ].map((option) => (
                <TouchableOpacity
                  key={option.label}
                  testID={`pronunciation-self-score-${option.score}`}
                  accessibilityLabel={option.label}
                  accessibilityRole="button"
                  onPress={() => handleSelfScore(option.score)}
                  activeOpacity={0.7}
                >
                  <Box
                    px="$3"
                    py="$2"
                    rounded="$md"
                    style={{ backgroundColor: t.isDark ? '#374151' : '#F3F4F6', borderWidth: 1, borderColor: t.border }}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: option.color, ...ffs }}
                    >
                      {option.label}
                    </Text>
                  </Box>
                </TouchableOpacity>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Voice placeholder */}
        {!completed && voiceEnabled && (
          <Box py="$3" alignItems="center">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: t.textSecondary, textAlign: 'center', ...ffs }}
            >
              Tap the microphone to record your pronunciation
            </Text>
          </Box>
        )}

        {/* Result */}
        {completed && score !== null && (
          <Box
            p="$2"
            rounded="$md"
            style={{ backgroundColor: score >= minScore ? '#D1FAE5' : '#FEF3C7' }}
          >
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_SM * fontScale,
                fontWeight: '600',
                color: score >= minScore ? '#065F46' : '#92400E',
                textAlign: 'center',
                ...ffs,
              }}
            >
              {score >= minScore ? '✓ Great pronunciation!' : 'Keep practicing this sound!'}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
});
